import * as React from 'react';
import { styled } from '@mui/material/styles';
import { findIndex } from 'lodash';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import faqs from 'assets/json/faq.json';
import { CurrentUser } from 'auth';

interface IStyledTabProps {
  label: string;
}

interface ITabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: ITabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
      style={{
        paddingLeft: 15,
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Divider sx={{ borderWidth: 1 }} />
      {value === index && (
        <Box sx={{ pt: 0, overflowX: 'hidden' }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

const AntTabs = styled(Tabs)({
  borderBottom: '1px solid #e8e8e8',
  '& .MuiTabs-indicator': {
    backgroundColor: '#1890ff',
    left: 0,
    width: 20,
  },
});

const AntTab = styled((props: IStyledTabProps) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    minWidth: 0,
    width: 400,
    alignItems: 'flex-start',
    textAlign: 'left',
    [theme.breakpoints.up('sm')]: {
      minWidth: 0,
    },
    fontWeight: theme.typography.fontWeightBold,
    paddingLeft: 40,
    margin: '5px 0',
    color: 'black',
    border: '2px solid #757575',
    '&:hover': {
      color: '#40a9ff',
      opacity: 1,
    },
    '&.Mui-selected': {
      color: '#1890ff',
    },
    '&.Mui-focusVisible': {
      backgroundColor: '#d1eaff',
    },
  }),
);

export default function Faq({ initialTabId }: { initialTabId?: string }) {
  const [tabId, setTabId] = React.useState(0);

  const schema = faqs.map((a) => {
    const locale = CurrentUser.getInstance().getLocale() || 'en-GB';
    return {
      id: a.id,
      title: a.title[locale],
      content: (
        <div
          dangerouslySetInnerHTML={{
            __html: a.content
              .map((b) => {
                return b[locale];
              })
              .join('<br /><br />'),
          }}
        ></div>
      ),
    };
  });

  React.useEffect(() => {
    const index = findIndex(schema, (e) => e.id === initialTabId);
    setTabId(index);
  }, [initialTabId]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabId(newValue);
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        bgcolor: 'background.paper',
        display: 'flex',
        height: 524,
      }}
    >
      <AntTabs
        orientation="vertical"
        variant="scrollable"
        value={tabId}
        onChange={handleChange}
        aria-label="Vertical tabs example"
      >
        {schema.map((s, i) => (
          <AntTab key={i} label={s.title} {...a11yProps(i)} />
        ))}
      </AntTabs>
      {schema.map((s, i) => (
        <TabPanel key={i} value={tabId} index={i}>
          {s.content}
        </TabPanel>
      ))}
    </Box>
  );
}
